import React, { useMemo } from 'react'; // Added useMemo
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/apiClient';
import { ApiResponse } from '../../types';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  Skeleton 
} from "@pagespeedify/ui";

interface Transaction {
  id: string;
  subscription_id: string; 
  amount: number; 
  currency: string; 
  status: string;
  provider: string;
  provider_transaction_id?: string | null; 
  description: string;
  transaction_date: string; 
  payment_method?: string | null;
  transaction_type?: string;
}

interface TransactionHistoryResponse {
  transactions: Transaction[];
}

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@pagespeedify/ui";
import { RefreshCw } from 'lucide-react'; // For retry button

export function InvoiceHistory({ userId, enabled = true }: { userId: string; enabled?: boolean }) {
  const apiClient = useApi();
  
  const { 
    data: historyResponse, 
    isLoading, 
    isError, 
    error,
    refetch, // Added for retry button
    isFetching // For background updates
  } = useQuery<ApiResponse<TransactionHistoryResponse>, Error>({
    queryKey: ['subscriptionHistory', userId],
    // Ensure the queryFn unwraps the ApiResponse correctly or handles it if the API returns the flat structure directly
    queryFn: async () => {
        const response = await apiClient.get<ApiResponse<TransactionHistoryResponse>>('/subscriptions/history');
        if (!response.success) {
            throw new Error(response.message || 'Failed to fetch subscription history (API error)');
        }
        // Assuming response.data contains TransactionHistoryResponse which has .transactions
        if (!response.data || !Array.isArray(response.data.transactions)) {
             throw new Error('Subscription history data is missing or not in expected format.');
        }
        return response; // Return the whole ApiResponse object
    },
    enabled: !!userId && enabled,
    retry: 0, // Consistent with CreditHistoryDisplay
    staleTime: 5 * 60 * 1000,  
    cacheTime: 15 * 60 * 1000, 
  });

  // Sort transactions by date, newest first (similar to CreditHistoryDisplay default)
  const sortedTransactions = useMemo(() => {
    if (!historyResponse?.data?.transactions) return [];
    return [...historyResponse.data.transactions].sort((a, b) =>
      new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime()
    );
  }, [historyResponse?.data?.transactions]);

  if (isLoading) {
    return (
      <>
        {/* Skeleton similar to CreditHistoryDisplay, suitable for direct embedding */}
        <Skeleton className="h-8 w-full mb-2" />
        <Skeleton className="h-8 w-full mb-2" />
        <Skeleton className="h-8 w-full" />
      </>
    );
  }
  
  if (isError) { // Handles network errors or if queryFn throws an error
    return (
      <div className="text-center py-8">
          <div className="text-red-600 dark:text-red-400 mb-2">Failed to load billing history</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {(error instanceof Error ? error.message : 'An unknown error occurred.')}
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />Retry
          </Button>
      </div>
    );
  }

  // This condition handles API returning success:false explicitly
  if (historyResponse && !historyResponse.success) {
    return (
      <div className="text-center py-8">
          <div className="text-red-600 dark:text-red-400 mb-2">Failed to load billing history</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {historyResponse.message || 'API returned an error.'}
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />Retry
          </Button>
      </div>
    );
  }
  
  if (!sortedTransactions || sortedTransactions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-600 dark:text-gray-400">
        No billing history available.
      </div>
    );
  }
  
  // Return table content directly, without Card shell
  return (
    <>
      {isFetching && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 animate-pulse">Updating history...</p>}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                <TableHead className="text-right whitespace-nowrap">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTransactions.map(transaction => (
                <TableRow key={transaction.id}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {new Date(transaction.transaction_date).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </TableCell>
                  <TableCell className="max-w-sm truncate hover:whitespace-normal">
                    {transaction.description || transaction.transaction_type || 'N/A'}
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: transaction.currency }).format(transaction.amount)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                      transaction.status === 'completed' || transaction.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100' :
                      transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-100' :
                      transaction.status === 'refunded' ? 'bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-100' :
                      'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-100' // Default/error status
                    }`}>
                      {transaction.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination could be added here if needed in the future, similar to CreditHistoryDisplay */}
    </>
  );
}